import { useState, useEffect, useRef, useCallback } from 'react'
import api from '../../lib/api'

function ProgressInline({ label, total, remaining }) {
  const done = Math.max(0, total - remaining)
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  return (
    <div className="sync-progress">
      <div className="progress-header">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="progress-bar">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="progress-stats">
        <span>{done.toLocaleString()} / {total.toLocaleString()} done</span>
        <span>{remaining.toLocaleString()} remaining</span>
      </div>
    </div>
  )
}

export function StepProcess({ socket, onAllDone }) {
  const [stats, setStats] = useState({ contactProcessing: { totalEmails: 0, unprocessedEmails: 0 }, sentimentAnalysis: { totalContacts: 0, unanalyzedContacts: 0 } })
  const [contactLive, setContactLive] = useState(null)
  const [sentimentLive, setSentimentLive] = useState(null)
  const [contactStatus, setContactStatus] = useState('idle') // idle | running | done | cancelled | failed
  const [sentimentStatus, setSentimentStatus] = useState('idle')
  const [cancellingContact, setCancellingContact] = useState(false)
  const [cancellingSentiment, setCancellingSentiment] = useState(false)
  const [error, setError] = useState('')
  const onAllDoneRef = useRef(onAllDone)
  const contactStatusRef = useRef(contactStatus)
  useEffect(() => { onAllDoneRef.current = onAllDone }, [onAllDone])
  useEffect(() => { contactStatusRef.current = contactStatus }, [contactStatus])

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/jobs/stats')
      setStats(res.data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    fetchStats()
    const id = setInterval(fetchStats, 5000)
    return () => clearInterval(id)
  }, [fetchStats])

  const startContacts = useCallback(async () => {
    setError('')
    setContactStatus('running')
    try {
      await api.post('/contacts/process')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start contact processing')
      setContactStatus('idle')
    }
  }, [])

  const startSentiment = useCallback(async () => {
    setError('')
    setSentimentStatus('running')
    try {
      await api.post('/contacts/sentiments/process')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start sentiment analysis')
      setSentimentStatus('idle')
    }
  }, [])

  // Auto-start contact processing on mount
  useEffect(() => {
    startContacts()
  }, [])

  // Auto-start sentiment when contact processing finishes
  useEffect(() => {
    if (contactStatus === 'done') {
      startSentiment()
    }
  }, [contactStatus, startSentiment])

  useEffect(() => {
    if (!socket) return

    const onContactProgress = (data) => setContactLive({ totalRemaining: data.totalRemaining })
    const onContactDone = (data) => {
      setContactLive(null)
      setContactStatus(data?.status === 'cancelled' ? 'cancelled' : 'done')
      setCancellingContact(false)
      fetchStats()
    }
    const onContactFailed = () => {
      setContactLive(null)
      setContactStatus('failed')
      setCancellingContact(false)
    }
    const onSentimentProgress = (data) => setSentimentLive({ totalRemaining: data.totalRemaining })
    const onSentimentDone = (data) => {
      setSentimentLive(null)
      setSentimentStatus(data?.status === 'cancelled' ? 'cancelled' : 'done')
      setCancellingSentiment(false)
      fetchStats()
      if (data?.status !== 'cancelled') onAllDoneRef.current?.()
    }
    const onSentimentFailed = () => {
      setSentimentLive(null)
      setSentimentStatus('failed')
      setCancellingSentiment(false)
    }

    socket.on('contact-job-progress', onContactProgress)
    socket.on('contact-job-complete', onContactDone)
    socket.on('contact-job-failed', onContactFailed)
    socket.on('sentiment-job-progress', onSentimentProgress)
    socket.on('sentiment-job-complete', onSentimentDone)
    socket.on('sentiment-job-failed', onSentimentFailed)

    return () => {
      socket.off('contact-job-progress', onContactProgress)
      socket.off('contact-job-complete', onContactDone)
      socket.off('contact-job-failed', onContactFailed)
      socket.off('sentiment-job-progress', onSentimentProgress)
      socket.off('sentiment-job-complete', onSentimentDone)
      socket.off('sentiment-job-failed', onSentimentFailed)
    }
  }, [socket, fetchStats])

  const cancelContact = async () => {
    if (cancellingContact) return
    setCancellingContact(true)
    try {
      await api.post('/contacts/process/cancel')
    } catch {
      setCancellingContact(false)
    }
  }

  const cancelSentiment = async () => {
    if (cancellingSentiment) return
    setCancellingSentiment(true)
    try {
      await api.post('/contacts/sentiments/process/cancel')
    } catch {
      setCancellingSentiment(false)
    }
  }

  const contactTotal = stats.contactProcessing.totalEmails
  const contactRemaining = contactLive
    ? Math.min(Math.max(0, contactLive.totalRemaining ?? 0), contactTotal)
    : stats.contactProcessing.unprocessedEmails

  const sentimentTotal = stats.sentimentAnalysis.totalContacts
  const sentimentRemaining = sentimentLive
    ? Math.min(Math.max(0, sentimentLive.totalRemaining ?? 0), sentimentTotal)
    : stats.sentimentAnalysis.unanalyzedContacts

  const contactsRunning = contactStatus === 'running' || !!contactLive
  const sentimentRunning = sentimentStatus === 'running' || !!sentimentLive

  return (
    <div className="onboarding-step">
      <h2>Step 4 — Classify contacts &amp; analyse sentiment</h2>
      <p className="step-hint">
        First we group emails by sender into your categories, then we generate a sentiment summary per contact.
      </p>

      {error && <div className="message error">{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <strong style={{ flex: 1 }}>Contact processing</strong>
            {contactsRunning && (
              <button className="btn btn-ghost" onClick={cancelContact} disabled={cancellingContact} style={{ fontSize: 13 }}>
                {cancellingContact ? 'Cancelling…' : 'Cancel'}
              </button>
            )}
            {contactStatus === 'done' && <span className="status-badge status-completed">Done</span>}
            {contactStatus === 'cancelled' && (
              <button className="btn btn-ghost" onClick={startContacts} style={{ fontSize: 13 }}>Retry</button>
            )}
            {contactStatus === 'failed' && (
              <button className="btn btn-ghost" onClick={startContacts} style={{ fontSize: 13 }}>Retry</button>
            )}
          </div>
          {(contactsRunning || contactTotal > 0) && (
            <ProgressInline label={contactsRunning ? 'Processing…' : contactStatus === 'cancelled' ? 'Cancelled' : contactStatus === 'failed' ? 'Failed' : 'Done'} total={contactTotal} remaining={contactRemaining} />
          )}
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <strong style={{ flex: 1 }}>Sentiment analysis</strong>
            {sentimentRunning && (
              <button className="btn btn-ghost" onClick={cancelSentiment} disabled={cancellingSentiment} style={{ fontSize: 13 }}>
                {cancellingSentiment ? 'Cancelling…' : 'Cancel'}
              </button>
            )}
            {sentimentStatus === 'done' && <span className="status-badge status-completed">Done</span>}
            {(sentimentStatus === 'cancelled' || sentimentStatus === 'failed') && (
              <button className="btn btn-ghost" onClick={startSentiment} style={{ fontSize: 13 }}>Retry</button>
            )}
          </div>
          {(sentimentRunning || sentimentTotal > 0) && (
            <ProgressInline label={sentimentRunning ? 'Analysing…' : sentimentStatus === 'cancelled' ? 'Cancelled' : sentimentStatus === 'failed' ? 'Failed' : 'Done'} total={sentimentTotal} remaining={sentimentRemaining} />
          )}
        </div>
      </div>
    </div>
  )
}
